import { createContext, useCallback, useReducer, useEffect } from "react";

import axios from "../../utils/axios/v1/userAxios";
import { SeverityLevel } from "../../utils/constants";
import { ALL_SITES_ID, isAllSites, rememberSingleSiteId } from "../../utils/globalSite";
import { isSiteEligibleForGlobalSelector } from "../../components/pages/_new/sites/addDomain2Gates";

const SET_INIT = "SET_INIT";
const GET_TRAFFICS = "GET_TRAFFICS";
const GET_REGIONAL_TRAFFICS = "GET_REGIONAL_TRAFFICS";
const GET_REGIONAL_DETECTIONS = "GET_REGIONAL_DETECTIONS";
const GET_TOP_REGIONAL_TRAFFICS = "GET_TOP_REGIONAL_TRAFFICS";
const GET_TOP_REGIONAL_DETECTIONS = "GET_TOP_REGIONAL_DETECTIONS";
const GET_TOP_DETECTION_TYPE = "GET_TOP_DETECTION_TYPE";
const GET_DETECTIONS_BY_WAF_TYPE = "GET_DETECTIONS_BY_WAF_TYPE";
const GET_DETECTIONS_SEVERITY_TIMESERIES = "GET_DETECTIONS_SEVERITY_TIMESERIES";
const GET_LATEST_DETECTS = "GET_LATEST_DETECTS";
const GET_WEB_SECURITY_SCORE_HISTORY = "GET_WEB_SECURITY_SCORE_HISTORY";
const GET_HOSTED_SCAN_DATA = "GET_HOSTED_SCAN_DATA";
const GET_EVENTS = "GET_EVENTS";
const GET_TOTAL_EVENTS_COUNT = "GET_TOTAL_EVENTS_COUNT";
const GET_SITES = "GET_SITES";
const GET_BASIS = "GET_BASIS";
const SET_CURRENT_STATUS = "SET_CURRENT_STATUS";
const GET_PROJECT_LIST = "GET_PROJECT_LIST";
const SET_CURRENT_PROJECT_ID = "SET_CURRENT_PROJECT_ID";
const SET_CURRENT_SITE_ID = "SET_CURRENT_SITE_ID";
const SET_SITE_SELECTOR_ENABLED = "SET_SITE_SELECTOR_ENABLED";
const SET_SITE_SELECTOR_SHOW_ALL_OPTION = "SET_SITE_SELECTOR_SHOW_ALL_OPTION";
const SET_SITE_SELECTOR_UI = "SET_SITE_SELECTOR_UI";
const GET_WAF_OVERVIEW = "GET_WAF_OVERVIEW";
const SET_ERROR = "SET_ERROR";

/** Normalized empty payload so UI does not treat errors as perpetual loading (`critical === undefined`). */
const EMPTY_HOSTEDSCAN_DATA = { data: [], critical: [], high: [], medium: [], low: [] };

const getStoredProjectId = () => {
  try {
    const stored = localStorage.getItem("curProjectId");
    return stored || "all";
  } catch {
    return "all";
  }
};

const getStoredSiteId = () => {
  try {
    const stored = localStorage.getItem("curSiteId");
    return stored || ALL_SITES_ID;
  } catch {
    return ALL_SITES_ID;
  }
};

const initialState = {
  site_id: "all",
  time_range: { period: "7d" },
  traffics: null,
  regional_traffics: null,
  regional_detections: null,
  top_regional_traffics: null,
  top_regional_detections: null,
  top_detection_type: null,
  sigs: null,
  mls: null,
  sdsigs: null,
  smartparses: null,
  detectionsSeverityTimeseries: null,
  latestDetects: null,
  webSecurityScoreHistory: null,
  hostedscan_data: {},
  sites: null,
  sitesCount: null,
  waf_violations: { now: null, past: null },
  total_requests: { now: null, past: null },
  total_bandwidth: {
    inbound: { now: null, past: null },
    outbound: { now: null, past: null },
  },
  events: null,
  total_events_count: 0,
  rows_per_page: 5,
  from: 0,
  projectList: null,
  sitesFromProjectId: {},
  structedProjectList: null,
  curProjectId: getStoredProjectId(),
  curSiteId: getStoredSiteId(),
  siteSelectorEnabled: true,
  siteSelectorShowAllOption: true,
  wafOverview: null,
  errMsg: null,
};

const HomeReducer = (state, action) => {
  switch (action.type) {
    case SET_INIT:
      return {
        ...state,
        traffics: null,
        regional_traffics: null,
        regional_detections: null,
        top_regional_traffics: null,
        top_regional_detections: null,
        top_detection_type: null,
        sigs: null,
        mls: null,
        sdsigs: null,
        smartparses: null,
        detectionsSeverityTimeseries: null,
        latestDetects: null,
        webSecurityScoreHistory: null,
        hostedscan_data: {},
        sitesCount: null,
        waf_violations: { now: null, past: null },
        total_requests: { now: null, past: null },
        total_bandwidth: {
          inbound: { now: null, past: null },
          outbound: { now: null, past: null },
        },
        events: null,
        total_events_count: 0,
      };
    case GET_TRAFFICS:
      return {
        ...state,
        traffics: action.payload.traffics,
      };
    case GET_REGIONAL_TRAFFICS:
      return {
        ...state,
        regional_traffics: action.payload.regional_traffics,
      };
    case GET_REGIONAL_DETECTIONS:
      return {
        ...state,
        regional_detections: action.payload.regional_detections,
      };
    case GET_TOP_REGIONAL_TRAFFICS:
      return {
        ...state,
        top_regional_traffics: action.payload.top_regional_traffics,
      };
    case GET_TOP_REGIONAL_DETECTIONS:
      return {
        ...state,
        top_regional_detections: action.payload.top_regional_detections,
      };
    case GET_TOP_DETECTION_TYPE:
      return {
        ...state,
        top_detection_type: action.payload.top_detection_type,
      };
    case GET_DETECTIONS_BY_WAF_TYPE:
      return {
        ...state,
        sigs: action.payload.sigs,
        mls: action.payload.mls,
        sdsigs: action.payload.sdsigs,
        smartparses: action.payload.smartparses,
      };
    case GET_DETECTIONS_SEVERITY_TIMESERIES:
      return {
        ...state,
        detectionsSeverityTimeseries: action.payload.detectionsSeverityTimeseries,
      };
    case GET_LATEST_DETECTS:
      return {
        ...state,
        latestDetects: action.payload.latestDetects,
      };
    case GET_WEB_SECURITY_SCORE_HISTORY:
      return {
        ...state,
        webSecurityScoreHistory: action.payload.webSecurityScoreHistory,
      };
    case GET_HOSTED_SCAN_DATA:
      return {
        ...state,
        hostedscan_data: action.payload.hostedscan_data,
      };
    case GET_EVENTS:
      return {
        ...state,
        events: action.payload.events,
      };
    case GET_TOTAL_EVENTS_COUNT:
      return {
        ...state,
        total_events_count: action.payload.total_events_count,
      };
    case GET_SITES:
      return {
        ...state,
        sites: action.payload.sites,
      };
    case SET_CURRENT_STATUS:
      return {
        ...state,
        site_id: action.payload.site_id,
        time_range: action.payload.time_range,
        rows_per_page: action.payload.rows_per_page,
        from: action.payload.from,
      };
    case GET_BASIS:
      return {
        ...state,
        sitesCount: action.payload.sitesCount,
        waf_violations: action.payload.waf_violations,
        total_requests: action.payload.total_requests,
        total_bandwidth: action.payload.total_bandwidth,
      };
    case GET_PROJECT_LIST:
      return {
        ...state,
        projectList: action.payload.projectList,
        sitesFromProjectId: action.payload.sitesFromProjectId,
        structedProjectList: action.payload.structedProjectList,
      };
    case SET_CURRENT_PROJECT_ID:
      return {
        ...state,
        curProjectId: action.payload.curProjectId,
        curSiteId: action.payload.curSiteId ?? state.curSiteId,
      };
    case SET_CURRENT_SITE_ID:
      return {
        ...state,
        curSiteId: action.payload.curSiteId,
      };
    case SET_SITE_SELECTOR_ENABLED:
      return {
        ...state,
        siteSelectorEnabled: action.payload.siteSelectorEnabled,
        curSiteId: action.payload.curSiteId ?? state.curSiteId,
      };
    case SET_SITE_SELECTOR_SHOW_ALL_OPTION:
      return {
        ...state,
        siteSelectorShowAllOption: action.payload.siteSelectorShowAllOption,
      };
    case SET_SITE_SELECTOR_UI:
      return {
        ...state,
        siteSelectorEnabled: action.payload.siteSelectorEnabled,
        siteSelectorShowAllOption: action.payload.siteSelectorShowAllOption,
        curSiteId: action.payload.curSiteId,
      };
    case GET_WAF_OVERVIEW:
      return {
        ...state,
        wafOverview: action.payload.wafOverview,
      };
    case SET_ERROR:
      return {
        ...state,
        errMsg: action.payload.errMsg,
      };
    default:
      return state;
  }
};

const HomeContext = createContext(null);

let eventController;

function HomeProvider({ children }) {
  const [state, dispatch] = useReducer(HomeReducer, initialState);

  const setErr = useCallback((msg) => {
    dispatch({
      type: SET_ERROR,
      payload: {
        errMsg: msg,
      },
    });
  }, []);

  const setCurSiteId = useCallback((siteId) => {
    if (!state.siteSelectorEnabled) return;
    const next = siteId || ALL_SITES_ID;
    if (next === state.curSiteId) return;
    rememberSingleSiteId(next);
    try {
      localStorage.setItem("curSiteId", next);
    } catch {
      // localStorage not available
    }
    dispatch({
      type: SET_CURRENT_SITE_ID,
      payload: { curSiteId: next },
    });
  }, [state.siteSelectorEnabled, state.curSiteId]);

  const applySiteSelectorUi = useCallback(
    ({ enabled, showAllOption, curSiteId: siteId }) => {
      const nextShowAll =
        showAllOption !== undefined ? showAllOption : state.siteSelectorShowAllOption;
      let nextSiteId = siteId;
      if (enabled === false) {
        nextSiteId = ALL_SITES_ID;
      } else if (nextSiteId === undefined || nextSiteId === null) {
        nextSiteId = getStoredSiteId();
      }

      if (
        state.siteSelectorEnabled === enabled &&
        state.siteSelectorShowAllOption === nextShowAll &&
        state.curSiteId === nextSiteId
      ) {
        return;
      }

      try {
        localStorage.setItem("curSiteId", nextSiteId);
      } catch {
        // ignore
      }

      dispatch({
        type: SET_SITE_SELECTOR_UI,
        payload: {
          siteSelectorEnabled: enabled,
          siteSelectorShowAllOption: nextShowAll,
          curSiteId: nextSiteId,
        },
      });
    },
    [state.siteSelectorEnabled, state.siteSelectorShowAllOption, state.curSiteId]
  );

  const setSiteSelectorEnabled = useCallback(
    (enabled) => {
      if (enabled && state.siteSelectorEnabled) return;
      if (!enabled && !state.siteSelectorEnabled && isAllSites(state.curSiteId)) return;
      applySiteSelectorUi({ enabled });
    },
    [state.siteSelectorEnabled, state.curSiteId, applySiteSelectorUi]
  );

  const setSiteSelectorShowAllOption = useCallback((showAllOption) => {
    dispatch({
      type: SET_SITE_SELECTOR_SHOW_ALL_OPTION,
      payload: { siteSelectorShowAllOption: showAllOption },
    });
  }, []);

  const setCurProjectId = useCallback((projectId) => {
    try {
      localStorage.setItem("curProjectId", projectId);
      localStorage.setItem("curSiteId", ALL_SITES_ID);
    } catch {
      // localStorage not available
    }
    dispatch({
      type: SET_CURRENT_PROJECT_ID,
      payload: {
        curProjectId: projectId,
        curSiteId: ALL_SITES_ID,
      },
    });
  }, []);

  const validateCurSiteForProject = useCallback(() => {
    const options = state.sitesFromProjectId?.[state.curProjectId] || [];
    const ids = new Set(options.map((s) => s?.site_id).filter(Boolean));
    if (state.siteSelectorEnabled === false) {
      if (state.curSiteId !== ALL_SITES_ID) {
        dispatch({
          type: SET_CURRENT_SITE_ID,
          payload: { curSiteId: ALL_SITES_ID },
        });
      }
      return;
    }
    if (isAllSites(state.curSiteId)) return;
    if (ids.has(state.curSiteId)) return;
    try {
      localStorage.setItem("curSiteId", ALL_SITES_ID);
    } catch {
      // ignore
    }
    dispatch({
      type: SET_CURRENT_SITE_ID,
      payload: { curSiteId: ALL_SITES_ID },
    });
  }, [state.curSiteId, state.curProjectId, state.sitesFromProjectId, state.siteSelectorEnabled]);

  useEffect(() => {
    validateCurSiteForProject();
  }, [validateCurSiteForProject]);

  const getTraffics = useCallback(
    async (site_id, time_range, signal) => {
      try {
        const response = await axios.post(
          "stats/traffic",
          {
            site_id: site_id,
            project_id: state.curProjectId,
            time_range: time_range,
          },
          { signal }
        );
        dispatch({
          type: GET_TRAFFICS,
          payload: {
            traffics: response.data.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TRAFFICS,
          payload: {
            traffics: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr, state.curProjectId]
  );

  const getRegionalDetections = useCallback(
    async (site_id, time_range, signal) => {
      try {
        const response = await axios.post(
          "stats/regional_detection",
          {
            site_id: site_id,
            project_id: state.curProjectId,
            time_range: time_range,
          },
          { signal }
        );
        dispatch({
          type: GET_REGIONAL_DETECTIONS,
          payload: {
            regional_detections: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_REGIONAL_DETECTIONS,
          payload: {
            regional_detections: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr, state.curProjectId]
  );
  const getTopRegionalTraffics = useCallback(
    async (site_id, time_range, signal) => {
      try {
        const response = await axios.post(
          "stats/top_region_detection",
          {
            site_id: site_id,
            project_id: state.curProjectId,
            time_range: time_range,
          },
          { signal }
        );
        dispatch({
          type: GET_TOP_REGIONAL_TRAFFICS,
          payload: {
            top_regional_traffics: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_REGIONAL_TRAFFICS,
          payload: {
            top_regional_traffics: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr, state.curProjectId]
  );
  const getTopRegionalDetections = useCallback(
    async (site_id, time_range, signal) => {
      try {
        const response = await axios.post(
          "stats/smart_parse_top_region_detection",
          {
            site_id: site_id,
            project_id: state.curProjectId,
            time_range: time_range,
          },
          { signal }
        );
        dispatch({
          type: GET_TOP_REGIONAL_DETECTIONS,
          payload: {
            top_regional_detections: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_REGIONAL_DETECTIONS,
          payload: {
            top_regional_detections: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr, state.curProjectId]
  );
  const getTopDetectionType = useCallback(
    async (site_id, time_range, size, signal) => {
      try {
        const response = await axios.post(
          "stats/combined_top_detection_type",
          {
            site_id: site_id,
            project_id: state.curProjectId,
            time_range: time_range,
            size: size || 10,
          },
          { signal }
        );
        const data = response.data?.data || response.data;
        const mapSeverityToLevel = (severity) => {
          if (!severity || typeof severity !== "string") return SeverityLevel.WARNING;
          switch (severity.toLowerCase()) {
            case "critical":
              return SeverityLevel.CRITICAL;
            case "high":
              return SeverityLevel.ERROR;
            case "medium":
              return SeverityLevel.WARNING;
            case "low":
              return SeverityLevel.NOTICE;
            default:
              return SeverityLevel.WARNING;
          }
        };
        const capitalizeSeverity = (severity) => {
          if (!severity || typeof severity !== "string") return "Medium";
          return severity.charAt(0).toUpperCase() + severity.slice(1).toLowerCase();
        };
        const combinedData = Array.isArray(data)
          ? data.map((item) => ({
              attack_type: item.type || item.msg || "unknown",
              count: item.count || 0,
              severity: mapSeverityToLevel(item.severity),
              severityLabel: capitalizeSeverity(item.severity),
              source: item.source,
            }))
          : [];
        dispatch({
          type: GET_TOP_DETECTION_TYPE,
          payload: {
            top_detection_type: combinedData,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_TOP_DETECTION_TYPE,
          payload: {
            top_detection_type: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr, state.curProjectId]
  );
  const getDetectionsByWafType = useCallback(
    async (site_id, time_range, signal) => {
      try {
        const response = await axios.post(
          "stats/detections_by_waf_type",
          {
            site_id: site_id,
            project_id: state.curProjectId,
            time_range: time_range,
            size: 10,
          },
          { signal }
        );
        const data = response.data;
        dispatch({
          type: GET_DETECTIONS_BY_WAF_TYPE,
          payload: {
            sigs: Array.isArray(data.signature) ? data.signature : [],
            mls: [],
            sdsigs: [],
            smartparses: Array.isArray(data.smart_parse) ? data.smart_parse : [],
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_DETECTIONS_BY_WAF_TYPE,
          payload: {
            sigs: [],
            mls: [],
            sdsigs: [],
            smartparses: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr, state.curProjectId]
  );

  const getDetectionsSeverityTimeseries = useCallback(
    async (site_id, time_range, signal) => {
      try {
        const payload = {
          site_id: site_id,
          project_id: state.curProjectId,
          time_range: time_range,
        };
        const response = await axios.post("stats/detections_severity_timeseries", payload, { signal });
        dispatch({
          type: GET_DETECTIONS_SEVERITY_TIMESERIES,
          payload: {
            detectionsSeverityTimeseries: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_DETECTIONS_SEVERITY_TIMESERIES,
          payload: {
            detectionsSeverityTimeseries: { critical: [], high: [], medium: [], low: [] },
          },
        });
        setErr(err.message);
      }
    },
    [setErr, state.curProjectId]
  );

  const getLatestDetects = useCallback(
    async (site_id, time_range, interval, signal) => {
      try {
        const payload = {
          site_id,
          project_id: state.curProjectId,
          time_range,
        };

        const response = await axios.post("stats/detections_severity_summary", payload, { signal });

        dispatch({
          type: GET_LATEST_DETECTS,
          payload: {
            latestDetects: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_LATEST_DETECTS,
          payload: {
            latestDetects: {},
          },
        });
        setErr(err.message);
      }
    },
    [setErr, state.curProjectId]
  );

  const getWebSecurityScoreHistory = useCallback(
    async (site_id, signal) => {
      try {
        const response = await axios.post("site/score_history", { site_id, project_id: state.curProjectId }, { signal });
        dispatch({
          type: GET_WEB_SECURITY_SCORE_HISTORY,
          payload: {
            webSecurityScoreHistory: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_WEB_SECURITY_SCORE_HISTORY,
          payload: {
            webSecurityScoreHistory: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr, state.curProjectId]
  );

  const getHostedscanData = useCallback(
    async (site_id, signal) => {
      try {
        const response = await axios.post("site/hostedscan_history", { site_id, project_id: state.curProjectId }, { signal });
        dispatch({
          type: GET_HOSTED_SCAN_DATA,
          payload: {
            hostedscan_data: response.data,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_HOSTED_SCAN_DATA,
          payload: {
            hostedscan_data: { ...EMPTY_HOSTEDSCAN_DATA },
          },
        });
        setErr(err.message);
      }
    },
    [setErr, state.curProjectId]
  );

  const getEvents = useCallback(
    async (site_id, time_range, rows_per_page, from, signal) => {
      if (eventController) eventController.abort();
      try {
        const response = await axios.post(
          "log/waf_event",
          {
            site_id: site_id,
            project_id: state.curProjectId,
            time_range: time_range,
            count: rows_per_page,
            from: from,
          },
          {
            signal,
          }
        );
        dispatch({
          type: GET_EVENTS,
          payload: {
            events: response.data.data,
          },
        });
        dispatch({
          type: GET_TOTAL_EVENTS_COUNT,
          payload: {
            total_events_count: response.data.total,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_EVENTS,
          payload: {
            events: [],
          },
        });
        dispatch({
          type: GET_TOTAL_EVENTS_COUNT,
          payload: {
            total_events_count: 0,
          },
        });
        setErr(err.message);
      }
    },
    [setErr, state.curProjectId]
  );

  const getOnlyEvents = useCallback(
    async (site_id, time_range, rows_per_page, from) => {
      dispatch({
        type: SET_CURRENT_STATUS,
        payload: {
          site_id: site_id,
          time_range: time_range,
          rows_per_page: rows_per_page,
          from: from,
        },
      });

      if (eventController) eventController.abort();
      eventController = new AbortController();
      const signal = eventController.signal;
      try {
        const response = await axios.post(
          "log/waf_event",
          {
            site_id: site_id,
            project_id: state.curProjectId,
            time_range: time_range,
            count: rows_per_page,
            from: from,
          },
          {
            signal,
          }
        );
        dispatch({
          type: GET_EVENTS,
          payload: {
            events: response.data.data,
          },
        });
        dispatch({
          type: GET_TOTAL_EVENTS_COUNT,
          payload: {
            total_events_count: response.data.total,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_EVENTS,
          payload: {
            events: [],
          },
        });
        dispatch({
          type: GET_TOTAL_EVENTS_COUNT,
          payload: {
            total_events_count: 0,
          },
        });
        setErr(err.message);
      }
    },
    [setErr, state.curProjectId]
  );
  const getSites = useCallback(async () => {
    try {
      const response = await axios.get("/site/basis");
      dispatch({
        type: GET_SITES,
        payload: {
          sites: response.data,
        },
      });
    } catch (err) {
      dispatch({
        type: GET_SITES,
        payload: {
          sites: [],
        },
      });
      setErr(err.message);
    }
  }, [setErr]);

  const getWafOverview = useCallback(async () => {
    try {
      dispatch({
        type: GET_WAF_OVERVIEW,
        payload: {
          wafOverview: null,
        },
      });
      const params = {};
      if (state.curProjectId) {
        params.project_id = state.curProjectId;
      }
      const response = await axios.get("/config/waf/overview", { params });
      dispatch({
        type: GET_WAF_OVERVIEW,
        payload: {
          wafOverview: response.data,
        },
      });
    } catch (err) {
      dispatch({
        type: GET_WAF_OVERVIEW,
        payload: {
          wafOverview: null,
        },
      });
      setErr(err.message);
    }
  }, [setErr, state.curProjectId]);

  const getBasicData = useCallback(
    async (site_id, time_range, signal) => {
      try {
        const response = await axios.post(
          "stats/basis",
          {
            site_id: site_id,
            project_id: state.curProjectId,
            time_range: time_range,
          },
          { signal }
        );
        dispatch({
          type: GET_BASIS,
          payload: {
            sites: response.data,
            sitesCount: response.data.websites,
            waf_violations: response.data.waf_violations,
            total_requests: response.data.total_requests,
            total_bandwidth: response.data.total_bandwidth,
          },
        });
      } catch (err) {
        if ("canceled" === err.message) return;
        dispatch({
          type: GET_BASIS,
          payload: {
            sitesCount: 0,
            waf_violations: { now: 0, past: 0 },
            total_requests: { now: 0, past: 0 },
            total_bandwidth: {
              inbound: { now: 0, past: 0 },
              outbound: { now: 0, past: 0 },
            },
          },
        });
        setErr(err.message);
      }
    },
    [setErr, state.curProjectId]
  );

  const isActiveSelectboxSite = (site) => isSiteEligibleForGlobalSelector(site);

  const buildProjectSiteMap = (projects) => {
    const result = {};

    const processProject = (project) => {
      // If this project doesn't exist in the map yet, initialize
      if (!result[project.id]) {
        result[project.id] = [];
      }

      // Add this project's sites
      if (project.sites && project.sites.length > 0) {
        result[project.id].push(...project.sites.filter(isActiveSelectboxSite));
      }

      // Process subprojects (recursively)
      if (project.subprojects && project.subprojects.length > 0) {
        project.subprojects.forEach((sub) => {
          processProject(sub); // make subproject its own key

          // Also merge subproject sites into parent project
          result[project.id].push(...(result[sub.id] || []));
        });
      }
    };

    projects.forEach(processProject);

    return result;
  };
  const getAllProjectBaseList = useCallback(async (forceRefresh = false) => {
    try {
      const response = await axios.get("/project/basis_all");
      const tmpProjects = response.data.projects;
      const nonProjectSites = (response.data.sites || []).filter(isActiveSelectboxSite);

      // Build project site map
      const projectSiteMap = buildProjectSiteMap(tmpProjects);
      
      // Collect all site_ids that belong to projects
      const projectSiteIds = new Set();
      tmpProjects?.forEach(project => {
        // Add direct project sites
        project.sites?.filter(isActiveSelectboxSite).forEach(site => {
          if (site?.site_id) {
            projectSiteIds.add(site.site_id);
          }
        });
        // Add subproject sites
        project.subprojects?.forEach(subproject => {
          subproject.sites?.filter(isActiveSelectboxSite).forEach(site => {
            if (site?.site_id) {
              projectSiteIds.add(site.site_id);
            }
          });
        });
      });
      
      // Filter nonProjectSites to only include sites that are NOT in any project
      const trulyUnassignedSites = nonProjectSites.filter(site => {
        return site?.site_id && !projectSiteIds.has(site.site_id);
      });
      
      // Create a deduplicated list of all sites for "all" projects
      const allSitesMap = new Map();
      
      // Add all sites from response.data.sites (includes both assigned and unassigned)
      nonProjectSites.forEach(site => {
        if (site?.site_id) {
          allSitesMap.set(site.site_id, site);
        }
      });
      
      // Convert map back to array for the "all" list
      const allSites = Array.from(allSitesMap.values());
      
      dispatch({
        type: GET_PROJECT_LIST,
        payload: {
          projectList: tmpProjects?.flatMap((p) => [p, ...(p.subprojects || [])]),
          sitesFromProjectId: {
            all: allSites,
            default: trulyUnassignedSites,
            ...projectSiteMap,
          },
          structedProjectList: [{ id: "all", name: "All projects" }, { id: "default", name: "Default" }, ...tmpProjects],
        },
      });
      return response?.data;
    } catch (err) {
      setErr(err.message);
      return { projects: [], sites: [] };
    }
  }, [setErr]);

  const setCurrentStatus = useCallback(
    async (setHomeController, site_id, time_range, rows_per_page, from, init = true) => {
      setErr(null);
      if (init) {
        dispatch({
          type: SET_INIT,
        });
      }
      const controller = new AbortController();
      const signal = controller.signal;
      setHomeController(controller);
      dispatch({
        type: SET_CURRENT_STATUS,
        payload: {
          site_id: site_id,
          time_range: time_range,
          rows_per_page: rows_per_page,
          from: from,
        },
      });
      getBasicData(site_id, time_range, signal);
      getRegionalDetections(site_id, time_range, signal);
      getTopRegionalTraffics(site_id, time_range, signal);
      getTopRegionalDetections(site_id, time_range, signal);
      getTopDetectionType(site_id, time_range, undefined, signal);
      getTraffics(site_id, time_range, signal);
      getDetectionsByWafType(site_id, time_range, signal);
      getEvents(site_id, time_range, rows_per_page, from, signal);
      getDetectionsSeverityTimeseries(site_id, time_range, signal);
      getLatestDetects(site_id, time_range, "1d", signal);
      getWebSecurityScoreHistory(site_id, signal);
      getHostedscanData(site_id, signal);
    },
    [
      setErr,
      getBasicData,
      getRegionalDetections,
      getTopRegionalTraffics,
      getTopRegionalDetections,
      getTopDetectionType,
      getTraffics,
      getDetectionsByWafType,
      getEvents,
      getDetectionsSeverityTimeseries,
      getLatestDetects,
      getWebSecurityScoreHistory,
      getHostedscanData,
    ]
  );

  useEffect(() => {
    getAllProjectBaseList();
  }, [getAllProjectBaseList]);

  return (
    <HomeContext.Provider
      value={{
        ...state,
        getTraffics,
        getRegionalDetections,
        getTopRegionalTraffics,
        getTopRegionalDetections,
        getDetectionsByWafType,
        getDetectionsSeverityTimeseries,
        getLatestDetects,
        getWebSecurityScoreHistory,
        getHostedscanData,
        getEvents,
        getOnlyEvents,
        getSites,
        getWafOverview,
        getBasicData,
        setCurrentStatus,
        getAllProjectBaseList,
        setErr,
        setCurProjectId,
        setCurSiteId,
        setSiteSelectorEnabled,
        setSiteSelectorShowAllOption,
        applySiteSelectorUi,
      }}
    >
      {children}
    </HomeContext.Provider>
  );
}

export { HomeContext, HomeProvider };
